/**
 * DFP Signal Anomaly Alerts Tab
 * Displays signal anomaly alerts from the DFP detection engine
 * with severity-based styling, filtering, and acknowledgment.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Tooltip,
  Slider,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  CellTower as CellTowerIcon,
  Wifi as WifiIcon,
  GpsFixed as GpsIcon,
  Bluetooth as BluetoothIcon,
  Shield as ShieldIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as AckIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  HelpOutline as HelpIcon,
  Block as BlockIcon,
  VerifiedUser as TrustedIcon,
  PlaylistAddCheck as WatchlistIcon,
} from '@mui/icons-material';
import {
  dfpAlertsService,
  DFPAlert,
  RFEntityDossier,
  getAnomalyLabel,
  getSignalTypeLabel,
} from '../../services/dfpAlertsService';
import { apiService } from '@signals/app-shell/services/api';
import PromoteToPacketDialog, { PromoteTarget } from '@signals/app-shell/components/intel/PromoteToPacketDialog';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import {
  bundleWatchlistDraft,
  selectedWatchlistIdentities,
  WatchlistBundle,
} from './watchlistIdentities';

// ---------------------------------------------------------------------------
// Verdict pipeline: state badge + evidence dossier + one-tap entity verdict
// ---------------------------------------------------------------------------

const VERDICT_STYLES: Record<string, { color: string; label: string }> = {
  CONFIRMED_THREAT: { color: '#f44336', label: 'CONFIRMED THREAT' },
  INCONCLUSIVE: { color: '#ff9800', label: 'INCONCLUSIVE' },
  WATCH: { color: '#2196f3', label: 'WATCH' },
  ESTABLISHED: { color: '#4caf50', label: 'ESTABLISHED' },
  NEW: { color: '#9e9e9e', label: 'NEW' },
};

/**
 * Signal-quality bar with thresholded coloring, à la Network Survey record
 * detail. `norm` maps the raw value to 0..1 fill; color reflects quality tier.
 */
const SignalBar: React.FC<{ label: string; value: number | null | undefined; unit: string; norm: number; tier: 'excellent' | 'good' | 'fair' | 'poor' }> =
  ({ label, value, unit, norm, tier }) => {
    if (value == null || Number.isNaN(value)) return null;
    const color = tier === 'excellent' ? '#4caf50' : tier === 'good' ? '#8bc34a' : tier === 'fair' ? '#ff9800' : '#f44336';
    const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.7rem' }}>
        <Typography variant="caption" sx={{ width: 42, opacity: 0.7 }}>{label}</Typography>
        <Typography variant="caption" sx={{ width: 62, fontWeight: 600, textAlign: 'right' }}>{value} {unit}</Typography>
        <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <Box sx={{ width: `${Math.max(3, Math.min(100, norm * 100))}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
        </Box>
        <Typography variant="caption" sx={{ width: 62, color, fontSize: '0.62rem' }}>{tierLabel}</Typography>
      </Box>
    );
  };

// Quality thresholds mirror the cellular record-detail scale.
const rsrpTier = (v: number) => v >= -80 ? 'excellent' : v >= -95 ? 'good' : v >= -110 ? 'fair' : 'poor';
const rsrqTier = (v: number) => v >= -10 ? 'excellent' : v >= -15 ? 'good' : v >= -20 ? 'fair' : 'poor';
const snrTier = (v: number) => v >= 20 ? 'excellent' : v >= 13 ? 'good' : v >= 0 ? 'fair' : 'poor';

/** Small glyph matching the corresponding map symbol, for color-keyed legend rows. */
const MapGlyph: React.FC<{ kind: 'diamond' | 'ring' | 'line' | 'dot'; color: string }> = ({ kind, color }) => {
  const base: React.CSSProperties = { display: 'inline-block', flexShrink: 0 };
  if (kind === 'diamond') {
    return <span style={{ ...base, width: 9, height: 9, backgroundColor: color, border: '1.5px solid #fff', transform: 'rotate(45deg)', boxShadow: `0 0 4px ${color}` }} />;
  }
  if (kind === 'ring') {
    return <span style={{ ...base, width: 10, height: 10, borderRadius: '50%', border: `2.5px solid ${color}`, backgroundColor: `${color}33` }} />;
  }
  if (kind === 'line') {
    return <span style={{ ...base, width: 14, height: 3, borderRadius: 2, backgroundColor: color }} />;
  }
  return <span style={{ ...base, width: 9, height: 9, borderRadius: '50%', backgroundColor: color, border: '1.5px solid #fff' }} />;
};

/** Resolve the ledger entity key from alert evidence (verdict alerts embed it). */
const entityKeyFromEvidence = (evidence: Record<string, any>, signalType: string): { type: string; id: string } | null => {
  const dossier = evidence?.verdict_dossier;
  if (dossier?.entity_type && dossier?.identifier) return { type: dossier.entity_type, id: dossier.identifier };
  if (signalType === 'wifi' || signalType === 'bluetooth') {
    const raw = evidence?.external_id || evidence?.bssid || evidence?.address;
    if (raw) {
      const nid = String(raw).toLowerCase().replace(/[^0-9a-f]/g, '');
      if (nid.length === 12) return { type: signalType, id: nid };
    }
  }
  return null;
};

/**
 * Evidence dossier for verdict-pipeline alerts: what validated, what failed,
 * fleet ledger history, AI rationale, and one-tap analyst verdict written
 * straight to the entity ledger (authoritative for all future verdicts).
 */
export interface EmitterLocation {
  alertId: number;
  identifier: string;
  label?: string | null;
  observerLat?: number | null;
  observerLon?: number | null;
  estimate: { latitude: number; longitude: number; method: string; observerCount: number; accuracyM: number | null } | null;
  track: Array<{ t: string; latitude: number; longitude: number; observers: number; single_observer: boolean }>;
  registeredTower?: { latitude: number; longitude: number; range_m: number | null } | null;
}

const VerdictPanel: React.FC<{
  alert: DFPAlert;
  evidence: Record<string, any>;
  onEmitterLocated?: (loc: EmitterLocation) => void;
}> = ({ alert, evidence, onEmitterLocated }) => {
  const [entity, setEntity] = useState<RFEntityDossier | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dossier = evidence?.verdict_dossier || {};
  const state: string = entity?.verdict_state || evidence?.verdict_state || '';
  const key = entityKeyFromEvidence(evidence, alert.signal_type);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    setLoading(true);
    dfpAlertsService.getEntityDossier(key.type, key.id)
      .then(d => {
        if (cancelled) return;
        setEntity(d);
        if (onEmitterLocated) {
          const tdoaP = d.tdoa_position;
          const rssiP = d.observations?.estimated_position || null;
          const est = tdoaP
            ? { latitude: tdoaP.estimated_lat, longitude: tdoaP.estimated_lon, method: tdoaP.method, observerCount: tdoaP.phone_count, accuracyM: tdoaP.accuracy_m ?? null }
            : rssiP
              ? { latitude: rssiP.latitude, longitude: rssiP.longitude, method: rssiP.method, observerCount: rssiP.observer_count, accuracyM: rssiP.accuracy_m ?? null }
              : null;
          onEmitterLocated({
            alertId: alert.id,
            identifier: key.id,
            label: d.label,
            observerLat: alert.latitude,
            observerLon: alert.longitude,
            estimate: est,
            track: d.position_track || [],
            registeredTower: d.registered_tower
              ? { latitude: d.registered_tower.latitude, longitude: d.registered_tower.longitude, range_m: d.registered_tower.range_m }
              : null,
          });
        }
      })
      .catch(() => { /* entity may not be in the ledger yet */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id]);

  const setVerdict = async (verdict: 'benign' | 'threat' | null) => {
    if (!key) return;
    setSaving(true); setError(null);
    try {
      await dfpAlertsService.setEntityVerdict(key.type, key.id, verdict);
      setEntity(prev => prev ? { ...prev, operator_verdict: verdict } : prev);
    } catch (e: any) {
      setError(e.message || 'Failed to save verdict');
    } finally {
      setSaving(false);
    }
  };

  if (!state && !key) return null;
  const style = VERDICT_STYLES[state] || { color: '#9e9e9e', label: state || 'UNKNOWN' };
  const ledger = entity || dossier.ledger || {};
  const cell = dossier.cell_groundtruth;
  const ble = dossier.ble_classification;
  const rules: string[] = dossier.detector_rules || [];
  const aiRationale = entity?.ai_rationale || (evidence?.rule === 'ai_adjudicated_threat' ? evidence?.verdict_reason : null);
  const opVerdict = entity?.operator_verdict;

  const obs = entity?.observations;
  const observers = obs?.observers || [];
  const rssiVals = observers.filter(o => o.max_rssi != null);
  const bestRssi = rssiVals.length ? Math.max(...rssiVals.map(o => o.max_rssi as number)) : null;
  const worstRssi = observers.filter(o => o.min_rssi != null).length
    ? Math.min(...observers.filter(o => o.min_rssi != null).map(o => o.min_rssi as number)) : null;
  // Prefer the persisted multi-phone TDOA fix (has accuracy); fall back to the
  // live RSSI-weighted centroid computed from raw observations.
  const tdoa = entity?.tdoa_position;
  const estPos = tdoa
    ? { lat: tdoa.estimated_lat, lon: tdoa.estimated_lon, method: tdoa.method, n: tdoa.phone_count, acc: tdoa.accuracy_m }
    : obs?.estimated_position
      ? { lat: obs.estimated_position.latitude, lon: obs.estimated_position.longitude, method: obs.estimated_position.method, n: obs.estimated_position.observer_count, acc: obs.estimated_position.accuracy_m ?? null }
      : null;
  const tower = entity?.registered_tower;
  const track = entity?.position_track || [];
  // Mirrors the strobing alert-pin color on the COP map (COPMapGL).
  const severityColor = (alert.severity || '').toLowerCase() === 'warning' ? '#ff9800'
    : (alert.severity || '').toLowerCase() === 'info' ? '#2196f3' : '#f44336';
  const fmtDT = (ts?: string | null) => ts ? new Date(ts).toLocaleString() : '—';
  const methodLabel = (m: string) =>
    m === 'rssi_weighted_centroid' || m === 'rssi_centroid' ? 'RSSI-weighted centroid'
      : m === 'wifi_rtt_trilateration' || m === 'rtt_trilateration' ? 'WiFi RTT trilateration'
        : m === 'dtdoa' ? 'Differential TDOA' : m;

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={style.label}
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${style.color}22`, color: style.color, border: `1px solid ${style.color}55` }} />
        {(evidence?.verdict_reason || entity?.verdict_reason) && (
          <Typography variant="caption" sx={{ opacity: 0.85 }}>{evidence?.verdict_reason || entity?.verdict_reason}</Typography>
        )}
        {loading && <CircularProgress size={12} />}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pl: 0.5 }}>
        {(ledger.known || entity) ? (
          <>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              <strong>First seen:</strong> {fmtDT(ledger.first_seen)}{'  '}·{'  '}
              <strong>Last seen:</strong> {fmtDT(ledger.last_seen)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              <strong>Fleet history:</strong>{' '}
              seen {ledger.day_count ?? '?'} day(s), {ledger.obs_count ?? '?'} obs
              {(ledger.distinct_observers ?? ledger.distinct_observer_count) != null && ` by ${ledger.distinct_observers ?? ledger.distinct_observer_count} device(s)`}
              {ledger.radius_m != null && `, radius ${Math.round(Number(ledger.radius_m))}m`}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            <strong>Fleet history:</strong> never seen before by any fleet device
          </Typography>
        )}
        {(bestRssi != null || worstRssi != null) && (
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            <strong>Signal range (7d):</strong> {worstRssi ?? '?'} to {bestRssi ?? '?'} dBm
            {observers.length > 0 && ` across ${observers.length} observer${observers.length > 1 ? 's' : ''}`}
          </Typography>
        )}
        {(estPos || track.length >= 2 || tower || (alert.latitude != null && alert.longitude != null)) && (
          <Box sx={{ mt: 0.25, p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>Map localization</Typography>
            {alert.latitude != null && alert.longitude != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <MapGlyph kind="ring" color={severityColor} />
                <Typography variant="caption" sx={{ color: severityColor }}>
                  <strong>Alert pin — observer GPS:</strong>{' '}
                  {Number(alert.latitude).toFixed(5)}, {Number(alert.longitude).toFixed(5)}
                  <span style={{ opacity: 0.65 }}> — where the detecting phone was, not the emitter</span>
                </Typography>
              </Box>
            )}
            {estPos && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <MapGlyph kind="diamond" color="#00e5ff" />
                <Typography variant="caption" sx={{ color: '#00e5ff' }}>
                  <strong>Emitter estimate:</strong>{' '}
                  {estPos.lat.toFixed(5)}, {estPos.lon.toFixed(5)}
                  {estPos.acc != null && ` ±${Math.round(estPos.acc)}m`}
                  {' '}— {methodLabel(estPos.method)} from {estPos.n} device{estPos.n > 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            {track.length >= 2 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <MapGlyph kind="line" color="#ffab40" />
                <Typography variant="caption" sx={{ color: '#ffab40' }}>
                  <strong>Movement track:</strong> {track.length} fixes,{' '}
                  {fmtDT(track[0].t)} → {fmtDT(track[track.length - 1].t)}
                  {track.some(p => !p.single_observer) && ` (${track.filter(p => !p.single_observer).length} multi-observer)`}
                </Typography>
              </Box>
            )}
            {tower && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <MapGlyph kind="dot" color="#4caf50" />
                <Typography variant="caption" sx={{ color: '#4caf50' }}>
                  <strong>Registered tower (OpenCelliD):</strong>{' '}
                  {Number(tower.latitude).toFixed(5)}, {Number(tower.longitude).toFixed(5)}
                  {tower.range_m != null && ` (range ${Math.round(Number(tower.range_m))}m`}
                  {tower.radio && `, ${tower.radio}`}
                  {tower.samples != null && `, ${tower.samples} samples`}
                  {tower.range_m != null && ')'}
                </Typography>
              </Box>
            )}
            {entity?.timing_advance && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <MapGlyph kind="dot" color="#ba68c8" />
                <Typography variant="caption" sx={{ color: '#ba68c8' }}>
                  <strong>Timing advance:</strong> TA {entity.timing_advance.ta} →
                  {' '}handset ≤ {Math.round(entity.timing_advance.range_m)}m from tower
                  {' '}(±{Math.round(entity.timing_advance.range_step_m)}m)
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {alert.signal_type === 'cellular' && (evidence.ss_rsrp != null || evidence.rsrp != null || evidence.ss_rsrq != null || evidence.ss_sinr != null) && (
          <Box sx={{ mt: 0.25, p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7 }}>Signal quality</Typography>
            {(() => { const v = evidence.ss_rsrp ?? evidence.rsrp; return v != null ? <SignalBar label="RSRP" value={v} unit="dBm" norm={(v + 120) / 76} tier={rsrpTier(v)} /> : null; })()}
            {evidence.ss_rsrq != null && <SignalBar label="RSRQ" value={evidence.ss_rsrq} unit="dB" norm={(evidence.ss_rsrq + 25) / 22} tier={rsrqTier(evidence.ss_rsrq)} />}
            {(evidence.ss_sinr ?? evidence.snr) != null && (() => { const v = evidence.ss_sinr ?? evidence.snr; return <SignalBar label="SNR" value={v} unit="dB" norm={(v + 10) / 40} tier={snrTier(v)} />; })()}
          </Box>
        )}
        {observers.length > 0 && (
          <Box sx={{ mt: 0.25, p: 0.75, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25, opacity: 0.7 }}>
              Observed by ({observers.length} fleet device{observers.length > 1 ? 's' : ''}, last 7d)
            </Typography>
            {observers.slice(0, 5).map(o => (
              <Typography key={o.device_id} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.65rem', opacity: 0.8 }}>
                {o.device_id.substring(0, 16)} · {o.sightings}x · avg {o.avg_rssi ?? '?'} dBm (peak {o.max_rssi ?? '?'}) · {fmtDT(o.first_seen)} → {fmtDT(o.last_seen)}
              </Typography>
            ))}
            {observers.length > 5 && (
              <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.6rem' }}>+{observers.length - 5} more</Typography>
            )}
          </Box>
        )}
        {cell && (
          <Typography variant="caption" sx={{ color: cell.match === 'KNOWN_TOWER' ? '#4caf50' : cell.match === 'NO_COVERAGE' ? '#9e9e9e' : '#f44336' }}>
            <strong>Cell ground truth:</strong> {String(cell.match || '').replace(/_/g, ' ').toLowerCase()}
            {cell.distance_km != null && ` (${Number(cell.distance_km).toFixed(1)}km from registered position)`}
          </Typography>
        )}
        {ble && (
          <Typography variant="caption" sx={{ color: ble.consumer_ecosystem ? '#4caf50' : ble.tracker_capable ? '#ff9800' : 'inherit' }}>
            <strong>BLE class:</strong> {ble.device_class || 'unknown'}
            {ble.company && ` — ${ble.company}`}
            {ble.tracker_capable && ' (tracker-capable)'}
          </Typography>
        )}
        {rules.length > 0 && (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            <strong>Evidence rules:</strong> {rules.join(', ')}
          </Typography>
        )}
        {aiRationale && (
          <Box sx={{ p: 1, mt: 0.25, borderRadius: 1, bgcolor: 'rgba(156,39,176,0.08)', border: '1px solid rgba(156,39,176,0.25)' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#ba68c8', display: 'block', mb: 0.25 }}>AI analysis</Typography>
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>{aiRationale}</Typography>
          </Box>
        )}
      </Box>

      {key && (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ opacity: 0.5, fontSize: '0.6rem' }}>Entity verdict:</Typography>
          {opVerdict ? (
            <>
              <Chip size="small"
                label={opVerdict === 'threat' ? '✓ Marked threat' : '✓ Marked benign'}
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                  bgcolor: opVerdict === 'threat' ? 'rgba(244,67,54,0.15)' : 'rgba(76,175,80,0.15)',
                  color: opVerdict === 'threat' ? '#f44336' : '#4caf50' }} />
              <Chip size="small" label="Undo" clickable disabled={saving}
                onClick={e => { e.stopPropagation(); setVerdict(null); }}
                sx={{ height: 18, fontSize: '0.6rem', opacity: 0.7, cursor: 'pointer' }} />
            </>
          ) : (
            <>
              <Tooltip title="Settle this entity as benign on the ledger — it will never alert again">
                <Chip size="small" label="Benign" clickable disabled={saving || !entity}
                  onClick={e => { e.stopPropagation(); setVerdict('benign'); }}
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50', cursor: 'pointer' }} />
              </Tooltip>
              <Tooltip title="Confirm this entity as a threat on the ledger — it stays confirmed">
                <Chip size="small" label="Threat" clickable disabled={saving || !entity}
                  onClick={e => { e.stopPropagation(); setVerdict('threat'); }}
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336', cursor: 'pointer' }} />
              </Tooltip>
            </>
          )}
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Box>
      )}
    </Box>
  );
};

// @ts-ignore — retained for future severity-based styling
const SEVERITY_CONFIG = {
  critical: { color: '#f44336', bg: 'rgba(244, 67, 54, 0.12)', icon: <ErrorIcon />, label: 'CRITICAL' },
  warning: { color: '#ff9800', bg: 'rgba(255, 152, 0, 0.12)', icon: <WarningIcon />, label: 'WARNING' },
  info: { color: '#2196f3', bg: 'rgba(33, 150, 243, 0.12)', icon: <InfoIcon />, label: 'INFO' },
}; void SEVERITY_CONFIG;

// Threat classification based on confidence score
const THREAT_STATUS = {
  confirmed: { color: '#f44336', bg: 'rgba(244, 67, 54, 0.15)', label: 'CONFIRMED', min: 0.75, icon: <ErrorIcon /> },
  probable:  { color: '#ff9800', bg: 'rgba(255, 152, 0, 0.15)', label: 'PROBABLE',  min: 0.50, icon: <WarningIcon /> },
  possible:  { color: '#42a5f5', bg: 'rgba(66, 165, 245, 0.12)', label: 'POSSIBLE',  min: 0.20, icon: <InfoIcon /> },
} as const;

function getThreatStatus(confidence: number | null | undefined) {
  const c = confidence ?? 0;
  if (c >= THREAT_STATUS.confirmed.min) return THREAT_STATUS.confirmed;
  if (c >= THREAT_STATUS.probable.min) return THREAT_STATUS.probable;
  return THREAT_STATUS.possible;
}

const SIGNAL_ICONS: Record<string, React.ReactNode> = {
  cellular: <CellTowerIcon fontSize="small" />,
  wifi: <WifiIcon fontSize="small" />,
  gnss: <GpsIcon fontSize="small" />,
  bluetooth: <BluetoothIcon fontSize="small" />,
};

const ALERT_EXPLANATIONS: Record<string, { title: string; what: string; risk: string; action: string; rules?: string[] }> = {
  imsi_catcher_suspect: {
    title: 'IMSI Catcher / Rogue Base Station',
    what: 'A cellular base station exhibiting characteristics of hostile infrastructure. Detection uses multiple signals: unknown Physical Cell ID (PCI) not in the reference database, foreign Mobile Country Code (MCC), null cell tower identity with valid RF, unlicensed NR-ARFCN frequencies, and unregistered MCC/MNC operator combinations.',
    risk: 'IMSI catchers intercept phone communications, track device IMSI/IMEI identifiers, monitor SMS/voice, and can inject surveillance payloads or force protocol downgrades. This is the highest-confidence RF threat.',
    action: 'Immediately report to security. Document the PCI, ARFCN, MCC/MNC, and location. Avoid making calls or sending SMS in the area. Move operators to a different location if possible.',
    rules: [
      'Unknown PCI (0.85-0.92): Physical Cell ID not found in reference database',
      'Foreign MCC (0.70): Mobile Country Code from a non-domestic network',
      'Null Cell Tower ID (0.60): Valid RF signal but no cell tower identity',
      'Unlicensed ARFCN (0.75): NR-ARFCN frequency outside licensed band allocations',
      'Unregistered Operator (0.40-0.55): MCC/MNC not in known operator database',
    ],
  },
  rogue_access_point: {
    title: 'Rogue Access Point',
    what: 'A WiFi access point with suspicious characteristics detected through multiple methods: locally-administered MAC address (LA-bit, software-generated), 802.11 Information Element analysis revealing foreign country codes or missing encryption, high transmit power with hidden SSID, or multichannel honeypot behavior (same SSID on 3+ channels simultaneously).',
    risk: 'Rogue APs can perform man-in-the-middle attacks, intercept credentials, conduct WiFi surveillance, and force device connections through captive portals. Foreign-country APs may indicate state-level surveillance equipment.',
    action: 'Do not connect to the network. Note the BSSID, SSID, channel, and country code. Check the Signal Intelligence panel for decoded 802.11 IE data. Compare against the known AP inventory for the area.',
    rules: [
      'LA-bit BSSID (0.30-0.90): Locally administered MAC address on AP',
      'Open Non-IoT AP (0.30): Unencrypted AP that is not a known IoT device',
      'Foreign Country AP (0.70): 802.11 IE country code is not "US"',
      'Hidden + High Power (0.60): Hidden SSID with TX power > 24 dBm',
      'IE Open Network (0.40): No RSN Information Element (unencrypted)',
      'Multichannel Honeypot (0.50-1.0): Same SSID broadcasting on 3+ channels',
    ],
  },
  personal_hotspot: {
    title: 'Personal Hotspot',
    what: 'A phone or cellular MiFi tethering access point detected inside or near a protected geofence. Identified by phone/MiFi SSID signatures (iPhone, Galaxy, Pixel, AndroidAP, MiFi, Jetpack, "Name\'s iPhone", etc.) or by a randomized (LA-bit) MAC broadcasting per-band tethering SSIDs (e.g. "Name_2G" / "Name_5G").',
    risk: 'An unauthorized personal hotspot in a protected area is an uncontrolled network egress point: it can bridge sensitive networks to the cellular carrier, exfiltrate data, and evade monitoring. It also reveals the presence of a specific personal device on site.',
    action: 'Locate the device and operator. Confirm whether the hotspot is authorized for the area. If not, direct that it be disabled. For a known threat device, add its BSSID/SSID (WiFi) or MAC (Bluetooth) to the Watchlist to force a critical alert on every future appearance.',
    rules: [
      'Hotspot SSID Signature (0.50-0.65): SSID matches a known phone/MiFi tethering name',
      'Randomized MAC + Band Suffix (0.40-0.55): LA-bit BSSID broadcasting a per-band tethering SSID',
    ],
  },
  evil_twin: {
    title: 'Evil Twin Attack',
    what: 'The same SSID has been observed on multiple different BSSIDs within a 30-second scan window. This indicates someone is spoofing a legitimate network name to lure devices into connecting to a malicious access point.',
    risk: 'Evil twin attacks capture credentials, intercept traffic, and can inject malware. Particularly dangerous when mimicking trusted networks (corporate WiFi, hotel networks, etc.).',
    action: 'Identify which BSSID is the legitimate AP. Alert all operators to disconnect from the SSID. Check if any devices auto-connected to the rogue AP. Review captured traffic for compromised credentials.',
    rules: [
      'Within-scan Evil Twin (0.50-1.0): Same SSID on multiple BSSIDs in 30s window',
    ],
  },
  connection_downgrade: {
    title: 'WiFi Connection Downgrade',
    what: 'The operator\'s device is connected to a WiFi AP that has suddenly dropped in capability — for example from 5 GHz / 866 Mbps to 2.4 GHz / 54 Mbps on the same SSID. This pattern indicates the device may have been forced onto a weaker (attacker-controlled) AP.',
    risk: 'Connection downgrade attacks force devices onto a lower-capability AP controlled by the adversary, enabling traffic interception while maintaining the illusion of being on the same network. The downgrade can also disable security features only available on the higher-capability link.',
    action: 'Verify the legitimate AP is still operational. Check if the downgrade is due to an evil twin. Compare the current BSSID against known AP inventory. If unauthorized, disconnect immediately.',
    rules: [
      'Connection Downgrade (0.50-0.75): Connected AP link speed/frequency dropped significantly compared to recent baseline',
    ],
  },
  gnss_spoofing: {
    title: 'GNSS Spoofing',
    what: 'GNSS signals exhibiting characteristics of spoofed (fake) navigation data. Detection uses satellite exclusion ratio analysis (receiver rejects injected signals) and clock coherence checks (anomalous code lock and time-of-week patterns).',
    risk: 'GNSS spoofing feeds false position data to devices, misdirecting navigation, corrupting geolocation analytics, and potentially luring operators to dangerous locations.',
    action: 'Compare reported position against known location. Check if multiple devices report similar anomalies. Cross-reference with cellular and WiFi positioning for ground truth.',
    rules: [
      'SV Exclusion Spoofing (0.55): High satellite exclusion ratio with adequate SVs in view',
      'Clock Coherence (0.40): Anomalous code lock + time-of-week patterns',
    ],
  },
  jamming_gnss: {
    title: 'GNSS Jamming',
    what: 'Carrier-to-noise ratio (C/N0) on GNSS satellites has dropped below the usable floor (~25 dB-Hz). The signals are too weak for reliable positioning, indicating RF interference or intentional jamming.',
    risk: 'GNSS jamming degrades positioning accuracy, causes navigation errors, and may indicate broader electronic warfare activity in the area.',
    action: 'Check the number of satellites affected. If multiple SVs have degraded C/N0 simultaneously, jamming is likely. Single-SV degradation may be atmospheric. Assess if the jamming is localized or widespread.',
    rules: [
      'CN0 Degradation (0.30-0.90): Signal-to-noise below threshold, confidence scales with severity',
    ],
  },
  shadowed_device: {
    title: 'Shadowed / Tracked Device',
    what: 'A BLE device matching a known tracking device manufacturer (Apple AirTag, Tile, Samsung SmartTag) has been detected near the operator. Identification uses OUI matching against tracker manufacturer databases.',
    risk: 'Indicates a covert BLE tracker physically placed on the operator or their vehicle for location surveillance.',
    action: 'Physically inspect vehicle, bags, and personal items for unknown BLE devices. Check device advertising patterns against known tracker signatures. If found, document and remove the device.',
    rules: [
      'Known Tracker OUI (0.60): BLE device matches AirTag/Tile/SmartTag manufacturer OUI',
    ],
  },
  unknown_device: {
    title: 'Unknown Persistent Device',
    what: 'An unrecognized BLE device that has been observed following an operator across multiple locations over time. Confidence increases with observation count and movement correlation.',
    risk: 'May indicate a covert surveillance device, custom tracker, or unauthorized equipment in the operator\'s environment.',
    action: 'Attempt to identify the device through OUI lookup and signal characteristics. Monitor if the device continues to follow. Physically search the area if confidence is high.',
    rules: [
      'Unknown Persistent Device (0.30-0.80): Confidence scales with observations and distance correlation',
    ],
  },
  mac_anomaly: {
    title: 'MAC Address Anomaly',
    what: 'A BLE device with an unregistered OUI (not in IEEE database) detected at close proximity (strong signal strength). The combination of unknown manufacturer and physical closeness warrants attention.',
    risk: 'May indicate a covert tracking device, spoofed hardware identity, or surveillance equipment masquerading as a consumer device.',
    action: 'Cross-reference the MAC OUI against manufacturer databases. Note the signal strength to estimate proximity. Check for patterns of repeated observation.',
    rules: [
      'Unknown OUI + Strong RSSI (0.25): Unregistered manufacturer at close range',
    ],
  },
  drone_detected: {
    title: 'Drone / UAS Detected',
    what: 'An RF signal matching a known drone manufacturer has been detected via two methods: FAA RemoteID broadcast (WiFi/BLE beacon with operator and location data), or BLE/WiFi advertisement matching DJI, Parrot, Skydio, Teal, Autel, Yuneec and other manufacturer OUI and naming patterns (DJI-, MAVIC-, Phantom_, etc.).',
    risk: 'Unauthorized drones near sensitive facilities pose surveillance, payload delivery, and safety risks. Even commercial drones may be conducting unauthorized reconnaissance or violating restricted airspace.',
    action: 'Identify the drone manufacturer and model from the alert evidence. Determine if the drone is authorized for the area. If unauthorized, initiate counter-UAS procedures per local SOP. Document BSSID/address, signal strength, and location.',
    rules: [
      'RemoteID Broadcast (0.50-1.0): FAA RemoteID beacon with operator/location data',
      'BLE/WiFi Drone Signature (0.40-0.90): OUI + name pattern matching against drone manufacturers',
    ],
  },
  cotraveler: {
    title: 'Co-Traveler Detected',
    what: 'An external device appearing in the operator\'s RF environment across multiple distinct locations over time. The system analyzes WiFi and Bluetooth signals every ~60 seconds to identify devices following the operator. When WiFi RTT (802.11mc) data is available, distance consistency is also checked -- a device maintaining constant 1-3 meter distance across locations is almost certainly traveling in the same vehicle.',
    risk: 'Indicates potential physical or electronic surveillance. The device may be intentionally shadowing the operator or placed in/on their vehicle.',
    action: 'Review the co-traveler\'s movement pattern and the number of co-located observations. Check if it matches known friendly devices. Conduct counter-surveillance if pattern persists across multiple locations.',
    rules: [
      'Spatial-temporal correlation (0.30-1.0): Confidence scales with observation count, location spread, and optional RTT distance consistency',
    ],
  },
  watchlist_hit: {
    title: 'Watchlist Hit',
    what: 'A device matching an analyst-created watchlist entry (BSSID, BLE address, SSID/name pattern, or cell identity) has been observed. Watchlist matches take precedence over every trust or exclusion rule.',
    risk: 'An identifier was flagged by an analyst as hostile or of-interest. Its reappearance means the flagged hardware is back in RF range of the force.',
    action: 'Review the watchlist entry\'s label and reason in the alert evidence. Locate the emitter (View on Map / multi-phone position). Follow the response procedure that prompted the watchlist entry.',
    rules: [
      'Watchlist Match: exact identifier or analyst-defined pattern; severity is set by the entry\'s severity floor',
    ],
  },
  ble_flood: {
    title: 'BLE Advertising Flood',
    what: 'A single collector observed an abnormally high number of distinct BLE addresses in a short window (hundreds per minute vs ~25/min normal), often with known spam names. This is the signature of Flipper Zero-style BLE spam or a deliberate BLE denial-of-service.',
    risk: 'Advertising floods swamp Bluetooth scanners, can crash or degrade nearby receivers (iOS popup spam, scanner DoS), and can be used to mask other BLE activity in the area.',
    action: 'Identify the affected collector and its location. Physically sweep for the transmitting device (Flipper, ESP32 board). Check whether other collectors in the area see the same flood to bound the emitter\'s position.',
    rules: [
      'Advertising Flood (0.55-0.99): distinct-address rate far above baseline; spam-name signatures and single-shot ratio push to critical',
    ],
  },
  abnormal_signal_strength: {
    title: 'Abnormal Signal Strength',
    what: 'Sentinel value readings (-127 dBm) or unknown SSID on the device\'s connected WiFi. These readings indicate the sensor may be malfunctioning or the device\'s WiFi stack has been tampered with.',
    risk: 'May indicate sensor tampering, device compromise, or nearby high-power emitter overloading the receiver.',
    action: 'Verify device hardware integrity. Compare against other devices in the area. Check for connected WiFi legitimacy.',
    rules: [
      'Sentinel Value Detection (0.15-0.20): -127 dBm readings or unknown connected SSID',
    ],
  },
  rogue_base_station: {
    title: 'Rogue Base Station / IMSI Catcher',
    what: 'A cellular base station broadcasting on licensed spectrum with invalid or missing operator identity (MCC/MNC 0/0, blank operator name). Legitimate towers always identify their carrier.',
    risk: 'IMSI catchers intercept phone communications, track device locations, and can inject surveillance payloads. This is the highest-confidence RF threat.',
    action: 'Immediately report to security. Document the PCI, ARFCN, and location. Avoid making calls or sending SMS in the area.',
  },
  rogue_ap: {
    title: 'Rogue Access Point / Evil Twin',
    what: 'A WiFi access point with suspicious characteristics: locally-administered MAC (software-generated), open security on a sensitive SSID, or a BSSID not seen before in the area.',
    risk: 'Rogue APs can perform man-in-the-middle attacks, intercept credentials, and conduct surveillance by monitoring connected devices.',
    action: 'Do not connect to the network. Note the BSSID and SSID. Check if the AP is in the WiGLE database or known AP inventory.',
  },
  jamming_wifi: {
    title: 'WiFi Jamming',
    what: 'Significant reduction in visible WiFi access points or uniformly degraded signal strength compared to baseline for this location.',
    risk: 'WiFi jamming can force devices onto cellular-only connections, disable security cameras, and disrupt communications.',
    action: 'Compare current AP count against historical baseline. Check if multiple devices report similar degradation.',
  },
  jamming_cellular: {
    title: 'Cellular Jamming',
    what: 'Abnormal cellular signal characteristics suggesting intentional interference with the cellular network.',
    risk: 'Cellular jamming disrupts voice, data, and emergency communications in the affected area.',
    action: 'Check if multiple devices are affected. Report to network security immediately.',
  },
  dwell_anomaly: {
    title: 'Dwell Anomaly',
    what: 'The operator\'s dwell pattern at this location deviates from their established pattern of life -- unusually long or short visit compared to baseline.',
    risk: 'May indicate the operator is under duress, being detained, or their routine has been compromised.',
    action: 'Check operator status. Compare against known schedule and historical patterns.',
  },
  ble_located: {
    title: 'BLE Device Located',
    what: 'A Bluetooth Low Energy device has been positioned using observations from multiple collection phones. When two or more phones detect the same BLE address simultaneously, their GPS positions and signal strengths are combined to estimate where the emitter is physically located.',
    risk: 'Provides actionable intelligence on the physical location of trackers, unknown devices, or co-traveler devices that don\'t self-report position. Particularly valuable for locating covert surveillance devices.',
    action: 'Review the estimated position on the map. Cross-reference with other anomalies (unknown device, tracker OUI) for the same address. If the device is flagged as suspicious, conduct a physical search at the estimated location.',
    rules: [
      'RSSI Centroid (2+ phones): Position estimated from signal-strength-weighted GPS positions',
      'Differential TDOA (2+ phones with nanosecond timing): Higher accuracy via time-difference-of-arrival',
    ],
  },
  wifi_located: {
    title: 'WiFi AP Located',
    what: 'A WiFi access point has been positioned using observations from multiple collection phones. Compares signal strength from different GPS positions to estimate the AP\'s physical location.',
    risk: 'Enables detection of mobile rogue APs. If an AP\'s estimated position doesn\'t match its historical location, it may be an evil twin deployed at a new site.',
    action: 'Compare estimated position against known AP inventory. Check if the AP has moved from its historical location. Investigate if it\'s flagged as rogue or evil twin.',
    rules: [
      'RSSI Centroid (2+ phones): AP position estimated from multi-phone signal strength',
    ],
  },
  cell_located: {
    title: 'Cell Tower Located',
    what: 'A cellular base station has been positioned using observations from multiple collection phones. Signal strength differences from known GPS positions triangulate the tower location.',
    risk: 'Most powerful IMSI catcher detection method: a real tower is at a fixed known location. If the triangulated position doesn\'t match the registered tower location, it\'s likely a rogue base station (Stingray).',
    action: 'Compare estimated position against the FCC tower database. A position mismatch of >500m is highly suspicious. Cross-reference with Unknown PCI or Foreign MCC alerts on the same cell.',
    rules: [
      'RSSI Centroid (2+ phones): Tower position estimated from multi-phone signal strength and timing advance',
    ],
  },
};

const SIGNAL_ABBREV: Record<string, string> = {
  cellular: 'Cell',
  wifi: 'WiFi',
  gnss: 'GNSS',
  bluetooth: 'BT',
};

const SIGNAL_DOT_COLOR: Record<string, string> = {
  cellular: '#ff9800',
  wifi: '#9c27b0',
  gnss: '#4caf50',
  bluetooth: '#2196f3',
};

type FeedbackVerdict = 'true_positive' | 'false_positive' | 'unclear';
type DfpExclusionIdentifierType = 'mac' | 'ip' | 'device_id' | 'ssid' | 'name' | 'other';
type DfpExclusionScope = 'cotraveler' | 'wifi' | 'all';

interface DfpExclusionDraft {
  alert: DFPAlert;
  identifier: string;
  identifier_type: DfpExclusionIdentifierType;
  applies_to: DfpExclusionScope;
  label: string;
  reason: string;
}

interface DfpTrustedWifiDraft {
  alert: DFPAlert;
  ssid: string;
  bssid: string;
  label: string;
  latitude: string;
  longitude: string;
  radius_m: string;
  security: string;
  gateway_ip: string;
  reason: string;
}

interface DfpWatchlistDraft extends WatchlistBundle {
  alert: DFPAlert;
}

const getAlertTarget = (alert: DFPAlert): string => {
  const ev = alert.evidence || {};
  if (ev.address) return ev.address;
  if (ev.bssid) return ev.bssid;
  if (ev.external_id) return ev.external_id;
  if (ev.pci !== undefined) return `PCI ${ev.pci}`;
  if (ev.ssid) return ev.ssid;
  return alert.device_id?.substring(0, 15) || '—';
};

const looksLikeMac = (value?: string | null): boolean => {
  if (!value) return false;
  return /^[0-9a-f]{2}([-:])(?:[0-9a-f]{2}\1){4}[0-9a-f]{2}$/i.test(value.trim()) ||
    /^[0-9a-f]{12}$/i.test(value.trim());
};

const looksLikeIp = (value?: string | null): boolean => {
  if (!value) return false;
  const trimmed = value.trim();
  return /^\d{1,3}(?:\.\d{1,3}){3}$/.test(trimmed) || trimmed.includes(':');
};

const inferIdentifierType = (identifier: string, source?: string): DfpExclusionIdentifierType => {
  if (looksLikeMac(identifier)) return 'mac';
  if (looksLikeIp(identifier)) return 'ip';
  if (source === 'ssid') return 'ssid';
  if (source === 'name') return 'name';
  if (source === 'device_id') return 'device_id';
  return 'other';
};

const firstString = (...values: any[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (value != null && typeof value !== 'object' && String(value).trim()) return String(value).trim();
  }
  return '';
};

const buildExclusionDraft = (alert: DFPAlert): DfpExclusionDraft => {
  const ev = alert.evidence || {};
  const candidates: Array<[string, string | undefined]> = [
    [firstString(ev.address, ev.bssid, ev.mac, ev.gateway_ip, ev.ip_address, ev.ip), undefined],
    [firstString(ev.stable_identifier, ev.external_id), 'device_id'],
    [firstString(ev.ssid), 'ssid'],
    [firstString(ev.name), 'name'],
    [firstString(alert.device_id), 'device_id'],
  ];
  const [identifier, source] = candidates.find(([value]) => !!value) || ['', undefined];
  const applies_to: DfpExclusionScope =
    alert.anomaly_type === 'cotraveler' ? 'cotraveler' :
      alert.signal_type === 'wifi' ? 'wifi' :
        'all';

  return {
    alert,
    identifier,
    identifier_type: inferIdentifierType(identifier, source),
    applies_to,
    label: `${getAnomalyLabel(alert.anomaly_type)} - ${getAlertTarget(alert)}`.slice(0, 120),
    reason: '',
  };
};

const buildTrustedWifiDraft = (alert: DFPAlert): DfpTrustedWifiDraft | null => {
  const ev = alert.evidence || {};
  const ssid = firstString(ev.ssid);
  const bssid = firstString(ev.bssid, ev.address);
  if (alert.signal_type !== 'wifi' || (!ssid && !bssid)) return null;

  return {
    alert,
    ssid,
    bssid,
    label: `${ssid || bssid} - trusted profile`.slice(0, 120),
    latitude: alert.latitude != null ? String(alert.latitude) : '',
    longitude: alert.longitude != null ? String(alert.longitude) : '',
    radius_m: '150',
    security: firstString(ev.security),
    gateway_ip: firstString(ev.gateway_ip, ev.gateway, ev.dhcp_gateway),
    reason: '',
  };
};

const buildWatchlistDraft = (alert: DFPAlert): DfpWatchlistDraft | null => {
  const ev = alert.evidence || {};
  const bundle = bundleWatchlistDraft({
    signal_type: alert.signal_type,
    address: firstString(ev.address, ev.mac),
    name: firstString(ev.name),
    bssid: firstString(ev.bssid),
    ssid: firstString(ev.ssid),
    bt_type: firstString(ev.bt_type, ev.type),
    is_randomized: ev.is_randomized,
    cell_tower_id: firstString(ev.cell_tower_id),
  });
  if (!bundle) return null;
  return {
    ...bundle,
    alert,
    label: firstString(ev.watchlist_label, ev.name, ev.ssid, bundle.label).slice(0, 120),
  };
};

interface DFPAlertsTabProps {
  onAlertClick?: (alert: DFPAlert, expanding: boolean) => void;
  selectedDeviceIds?: string[];
  deviceIdToEntity?: Record<string, string>;
  /**
   * Look-back window in hours, sourced from the COP top-bar date dropdown.
   * Defaults to 24h to match the pre-filter behavior. Wider windows pull
   * proportionally more alerts so the panel and timeline aren't truncated.
   */
  hours?: number;
  /**
   * Alert tier filter. 'actionable' = high-precision threat feed (watchlist /
   * cotraveler / proximity); 'insights' = the noisier algorithmic anomaly feed;
   * undefined/'all' = everything (legacy behaviour).
   */
  category?: 'actionable' | 'insights' | 'all';
  /** Fired when an expanded alert resolves an emitter position estimate/track,
   *  so the host map can plot the estimate pin, accuracy ring, and path. */
  onEmitterLocated?: (loc: EmitterLocation) => void;
}

const DFPAlertsTab: React.FC<DFPAlertsTabProps> = ({ onAlertClick, selectedDeviceIds, deviceIdToEntity, hours = 24, category, onEmitterLocated }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const intelEnabled = useFeatureFlagsStore((s) => s.intel);
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);

  const [alerts, setAlerts] = useState<DFPAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [alertExplainType, setAlertExplainType] = useState<string | null>(null);

  // Explanation & feedback state
  const [explanations, setExplanations] = useState<Record<number, any>>({});
  const [loadingExplanation, setLoadingExplanation] = useState<number | null>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<Record<number, string>>({});
  const [feedbackLoading, setFeedbackLoading] = useState<number | null>(null);
  const [exclusionDraft, setExclusionDraft] = useState<DfpExclusionDraft | null>(null);
  const [trustedWifiDraft, setTrustedWifiDraft] = useState<DfpTrustedWifiDraft | null>(null);
  const [watchlistDraft, setWatchlistDraft] = useState<DfpWatchlistDraft | null>(null);
  const [quickActionSaving, setQuickActionSaving] = useState(false);
  const [quickActionError, setQuickActionError] = useState<string | null>(null);

  // Filters
  const [severityFilter] = useState<string>('');
  const [signalTypeFilter, setSignalTypeFilter] = useState<string>('');
  const [anomalyTypeFilter, setAnomalyTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [confidenceRange, setConfidenceRange] = useState<[number, number]>([75, 100]);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await dfpAlertsService.getAlerts({
        severity: severityFilter || undefined,
        signal_type: signalTypeFilter || undefined,
        anomaly_type: anomalyTypeFilter || undefined,
        category: category || undefined,
        // Default view: everything an operator has NOT actioned yet (open +
        // auto-resolved). The engine auto-resolves stale alerts within
        // minutes, so filtering on 'open' made the panel look empty after a
        // refresh even with dozens of recent alerts. An alert only leaves
        // this view when a human acknowledges/dismisses it. 'all' additionally
        // includes acknowledged + false-positive history.
        status: 'unacknowledged',
        hours,
        // Pull the full window each refresh; backend caps at 5000. Without
        // this the panel and Signal Threat Timeline truncate at 200 even
        // though wider windows can easily contain 800+ open alerts.
        limit: 5000,
      });
      setAlerts(resp.alerts);
      const submitted: Record<number, string> = {};
      resp.alerts.forEach(alert => {
        if (alert.feedback_verdict) submitted[alert.id] = alert.feedback_verdict;
      });
      setFeedbackSubmitted(submitted);
    } catch (err: any) {
      setError(err.message || 'Failed to load DFP alerts');
    } finally {
      setLoading(false);
    }
  }, [severityFilter, signalTypeFilter, anomalyTypeFilter, category, hours]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Background refresh: 60s cadence, paused while the browser tab is
  // hidden. Previous cadence was 15s which fired ~240 refreshes/hr against
  // a 5,000-alert payload — each refresh re-runs filter/severity/geofence
  // PIP work on the in-memory list and was the main source of "the laptop
  // fan spins up when I open this tab". 60s is plenty for analyst SA and
  // skipping while hidden means an open-but-backgrounded tab costs zero.
  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadAlerts();
    }, 60000);

    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        loadAlerts();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadAlerts]);

  const handleAcknowledge = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await dfpAlertsService.acknowledgeAlert(id, 'acknowledged');
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch (err: any) {
      console.error('Failed to acknowledge:', err);
    }
  };

  const handleLoadExplanation = async (alertId: number) => {
    if (explanations[alertId]) return;
    setLoadingExplanation(alertId);
    try {
      const explanation = await dfpAlertsService.getExplanation(alertId);
      setExplanations(prev => ({ ...prev, [alertId]: explanation }));
    } catch (err) {
      console.error('Failed to load explanation:', err);
    } finally {
      setLoadingExplanation(null);
    }
  };

  const handleFeedback = async (alertId: number, verdict: FeedbackVerdict, e: React.MouseEvent) => {
    e.stopPropagation();
    if (feedbackLoading === alertId) return;
    setFeedbackLoading(alertId);
    try {
      await dfpAlertsService.submitFeedback(alertId, verdict);
      setFeedbackSubmitted(prev => ({ ...prev, [alertId]: verdict }));
      setAlerts(prev => {
        const updated = prev.map(alert => alert.id === alertId
          ? {
              ...alert,
              feedback_verdict: verdict,
              status: verdict === 'false_positive' ? 'false_positive' : alert.status,
              resolved_at: verdict === 'false_positive' ? (alert.resolved_at || new Date().toISOString()) : alert.resolved_at,
            }
          : alert);
        return verdict === 'false_positive'
          ? updated.filter(alert => alert.id !== alertId)
          : updated;
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setFeedbackLoading(null);
    }
  };

  const openExclusionDialog = (alert: DFPAlert, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickActionError(null);
    setExclusionDraft(buildExclusionDraft(alert));
  };

  const openTrustedWifiDialog = (alert: DFPAlert, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickActionError(null);
    const draft = buildTrustedWifiDraft(alert);
    if (!draft) {
      setQuickActionError('This alert does not include WiFi SSID/BSSID data for a trusted profile.');
      return;
    }
    setTrustedWifiDraft(draft);
  };

  const submitExclusion = async () => {
    if (!exclusionDraft || !exclusionDraft.identifier.trim() || !exclusionDraft.reason.trim()) return;
    const draft = exclusionDraft;
    setQuickActionSaving(true);
    setQuickActionError(null);
    try {
      await apiService.client.post('/admin/dfp/exclusions', {
        identifier: draft.identifier.trim(),
        identifier_type: draft.identifier_type,
        applies_to: draft.applies_to,
        label: draft.label.trim() || null,
        reason: draft.reason.trim(),
        enabled: true,
      }, { timeout: 30000 });
      await dfpAlertsService.submitFeedback(draft.alert.id, 'false_positive', `Excluded known identifier: ${draft.reason.trim()}`);
      setFeedbackSubmitted(prev => ({ ...prev, [draft.alert.id]: 'false_positive' }));
      setAlerts(prev => prev.filter(alert => alert.id !== draft.alert.id));
      setExclusionDraft(null);
      await loadAlerts();
    } catch (err: any) {
      setQuickActionError(err?.response?.data?.detail || err?.message || 'Failed to add DFP exclusion');
    } finally {
      setQuickActionSaving(false);
    }
  };

  const submitTrustedWifi = async () => {
    if (!trustedWifiDraft || !trustedWifiDraft.ssid.trim() || !trustedWifiDraft.reason.trim()) return;
    const draft = trustedWifiDraft;
    setQuickActionSaving(true);
    setQuickActionError(null);
    try {
      await apiService.client.post('/admin/dfp/trusted-wifi', {
        ssid: draft.ssid.trim(),
        bssid: draft.bssid.trim() || null,
        label: draft.label.trim() || null,
        latitude: draft.latitude.trim() ? Number(draft.latitude) : null,
        longitude: draft.longitude.trim() ? Number(draft.longitude) : null,
        radius_m: Number(draft.radius_m) || 150,
        security: draft.security.trim() || null,
        gateway_ip: draft.gateway_ip.trim() || null,
        reason: draft.reason.trim(),
        enabled: true,
      }, { timeout: 30000 });
      await dfpAlertsService.submitFeedback(draft.alert.id, 'false_positive', `Trusted WiFi profile: ${draft.reason.trim()}`);
      setFeedbackSubmitted(prev => ({ ...prev, [draft.alert.id]: 'false_positive' }));
      setAlerts(prev => prev.filter(alert => alert.id !== draft.alert.id));
      setTrustedWifiDraft(null);
      await loadAlerts();
    } catch (err: any) {
      setQuickActionError(err?.response?.data?.detail || err?.message || 'Failed to add trusted WiFi profile');
    } finally {
      setQuickActionSaving(false);
    }
  };

  const openWatchlistDialog = (alert: DFPAlert, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickActionError(null);
    const draft = buildWatchlistDraft(alert);
    if (!draft) {
      setQuickActionError('This alert does not include a device identifier to watchlist.');
      return;
    }
    setWatchlistDraft(draft);
  };

  const submitWatchlist = async () => {
    if (!watchlistDraft) return;
    const chosen = selectedWatchlistIdentities(watchlistDraft);
    if (!chosen.length) return;
    setQuickActionSaving(true);
    setQuickActionError(null);
    const failed: string[] = [];
    let added = 0;
    try {
      for (const row of chosen) {
        try {
          await apiService.client.post('/admin/dfp/watchlist', {
            identifier: row.identifier.trim(),
            identifier_type: row.identifier_type,
            match_mode: 'exact',
            signal_type: row.signal_type,
            severity_floor: watchlistDraft.severity_floor,
            label: watchlistDraft.label.trim() || null,
            reason: watchlistDraft.reason.trim() || null,
            enabled: true,
          }, { timeout: 30000 });
          added += 1;
        } catch {
          failed.push(row.identifier);
        }
      }
      if (added === 0) {
        setQuickActionError(`Failed to add ${failed.join(', ')}`);
        return;
      }
      setWatchlistDraft(null);
    } finally {
      setQuickActionSaving(false);
    }
  };

  const filtered = useMemo(() => {
    let result = alerts.filter(a => {
      const conf = (a.confidence_score ?? 0) * 100;
      return conf >= confidenceRange[0] && conf <= confidenceRange[1];
    });
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(a =>
        a.anomaly_type.toLowerCase().includes(term) ||
        a.device_id.toLowerCase().includes(term) ||
        a.signal_type.toLowerCase().includes(term) ||
        (a.evidence?.description || '').toLowerCase().includes(term)
      );
    }
    if (selectedDeviceIds && selectedDeviceIds.length > 0) {
      result = result.filter(a => selectedDeviceIds.includes(String(a.device_id)));
    }
    return result;
  }, [alerts, searchTerm, confidenceRange, selectedDeviceIds]);

  const threatCounts = useMemo(() => ({
    confirmed: alerts.filter(a => (a.confidence_score ?? 0) >= 0.75).length,
    probable: alerts.filter(a => (a.confidence_score ?? 0) >= 0.50 && (a.confidence_score ?? 0) < 0.75).length,
    possible: alerts.filter(a => (a.confidence_score ?? 0) >= 0.20 && (a.confidence_score ?? 0) < 0.50).length,
  }), [alerts]);

  const counts = useMemo(() => ({
    critical: alerts.filter(a => a.severity === 'critical').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    info: alerts.filter(a => a.severity === 'info').length,
  }), [alerts]); void counts;

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return d.toLocaleDateString();
  };

  const formatTimeCompact = (ts: string) => {
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h`;
    return `${Math.floor(mins / 1440)}d`;
  };

  // Fully-qualified local datetime (e.g. "Apr 28, 2026 8:55:32 PM EDT").
  // Used in tooltips on relative ages and inline in the expanded detail
  // so analysts can see exactly when an alert fired / cleared.
  const formatExact = (ts?: string | null) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ShieldIcon sx={{ color: '#2196f3', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight="bold">Signal Alerts</Typography>
          <Tooltip title="How alerts work">
            <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ ml: -0.5 }}>
              <HelpIcon fontSize="small" sx={{ opacity: 0.6 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Threat status summary chips */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
        {threatCounts.confirmed > 0 && (
          <Chip size="small" label={`${threatCounts.confirmed} Confirmed`}
            sx={{ bgcolor: THREAT_STATUS.confirmed.bg, color: THREAT_STATUS.confirmed.color, fontWeight: 'bold', fontSize: '0.7rem' }}
            onClick={() => setConfidenceRange(confidenceRange[0] === 75 && confidenceRange[1] === 100 ? [20, 100] : [75, 100])}
            variant={confidenceRange[0] === 75 && confidenceRange[1] === 100 ? 'filled' : 'outlined'} />
        )}
        {threatCounts.probable > 0 && (
          <Chip size="small" label={`${threatCounts.probable} Probable`}
            sx={{ bgcolor: THREAT_STATUS.probable.bg, color: THREAT_STATUS.probable.color, fontWeight: 'bold', fontSize: '0.7rem' }}
            onClick={() => setConfidenceRange(confidenceRange[0] === 50 && confidenceRange[1] === 74 ? [20, 100] : [50, 74])}
            variant={confidenceRange[0] === 50 && confidenceRange[1] === 74 ? 'filled' : 'outlined'} />
        )}
        {threatCounts.possible > 0 && (
          <Chip size="small" label={`${threatCounts.possible} Possible`}
            sx={{ bgcolor: THREAT_STATUS.possible.bg, color: THREAT_STATUS.possible.color, fontSize: '0.7rem' }}
            onClick={() => setConfidenceRange(confidenceRange[0] === 20 && confidenceRange[1] === 49 ? [20, 100] : [20, 49])}
            variant={confidenceRange[0] === 20 && confidenceRange[1] === 49 ? 'filled' : 'outlined'} />
        )}
      </Box>

      {/* Confidence range slider */}
      <Box sx={{ px: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Confidence Range
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: '#42a5f5' }}>
            {confidenceRange[0]}% - {confidenceRange[1]}% ({filtered.length})
          </Typography>
        </Box>
        <Slider
          value={confidenceRange}
          onChange={(_, v) => setConfidenceRange(v as [number, number])}
          min={0}
          max={100}
          step={5}
          marks={[
            { value: 20, label: '' },
            { value: 50, label: '' },
            { value: 75, label: '' },
            { value: 100, label: '' },
          ]}
          size="small"
          sx={{
            mt: -0.5,
            mb: -0.5,
            height: 4,
            '& .MuiSlider-thumb': { width: 14, height: 14 },
            '& .MuiSlider-track': {
              background: `linear-gradient(90deg, ${THREAT_STATUS.possible.color}, ${THREAT_STATUS.probable.color}, ${THREAT_STATUS.confirmed.color})`,
            },
            '& .MuiSlider-rail': { opacity: 0.2 },
            '& .MuiSlider-mark': { bgcolor: 'text.secondary', opacity: 0.3, width: 2, height: 8 },
          }}
        />
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
        <FormControl size="small" sx={{ minWidth: 88 }}>
          <InputLabel>Signal</InputLabel>
          <Select value={signalTypeFilter} onChange={e => setSignalTypeFilter(e.target.value)} label="Signal">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="cellular">Cellular</MenuItem>
            <MenuItem value="wifi">WiFi</MenuItem>
            <MenuItem value="gnss">GNSS</MenuItem>
            <MenuItem value="bluetooth">Bluetooth</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 105 }}>
          <InputLabel>Type</InputLabel>
          <Select value={anomalyTypeFilter} onChange={e => setAnomalyTypeFilter(e.target.value)} label="Type">
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="imsi_catcher_suspect">Rogue Base Station</MenuItem>
            <MenuItem value="rogue_access_point">Rogue AP</MenuItem>
            <MenuItem value="personal_hotspot">Personal Hotspot</MenuItem>
            <MenuItem value="evil_twin">Evil Twin</MenuItem>
            <MenuItem value="cotraveler">Co-Traveler</MenuItem>
            <MenuItem value="drone_detected">Drone</MenuItem>
            <MenuItem value="unknown_device">Unknown Device</MenuItem>
            <MenuItem value="mac_anomaly">MAC Anomaly</MenuItem>
            <MenuItem value="gnss_spoofing">GNSS Spoofing</MenuItem>
            <MenuItem value="jamming_gnss">GNSS Jamming</MenuItem>
            <MenuItem value="ble_located">BLE Located</MenuItem>
            <MenuItem value="wifi_located">WiFi Located</MenuItem>
            <MenuItem value="cell_located">Cell Located</MenuItem>
            <MenuItem value="watchlist_hit">Watchlist Hit</MenuItem>
            <MenuItem value="ble_flood">BLE Flood</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" placeholder="Search..." value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)} sx={{ flex: 1, minWidth: 60 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ opacity: 0.5 }} /></InputAdornment> }} />
      </Box>
      {(signalTypeFilter || anomalyTypeFilter) && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75 }}>
          <Chip size="small" label="Clear Filters" variant="outlined"
            onDelete={() => { setSignalTypeFilter(''); setAnomalyTypeFilter(''); }}
            sx={{ height: 22, fontSize: '0.65rem' }} />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {quickActionError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setQuickActionError(null)}>{quickActionError}</Alert>}

      {/* Alert list */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: isDark
            ? 'linear-gradient(180deg, #42a5f5 0%, #1565c0 100%)'
            : 'linear-gradient(180deg, #0097a7 0%, #006070 100%)',
          borderRadius: '4px',
          '&:hover': {
            background: isDark
              ? 'linear-gradient(180deg, #64b5f6 0%, #1976d2 100%)'
              : 'linear-gradient(180deg, #00b8c9 0%, #007a8c 100%)',
          },
        },
        scrollbarWidth: 'thin',
        scrollbarColor: isDark ? '#42a5f5 transparent' : '#0097a7 transparent',
      }}>
        {loading && alerts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={30} /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 4, opacity: 0.7 }}>
            <ShieldIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ opacity: 0.5 }}>No unacknowledged signal alerts in this time window</Typography>
          </Box>
        ) : (
          /* ======== TABLE VIEW ======== */
          <Box>
            {/* Table header */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
              <Box sx={{ width: 16, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ width: 125, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5 }}>Type</Typography>
              <Typography variant="caption" sx={{ width: 42, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5 }}>Signal</Typography>
              <Typography variant="caption" sx={{ flex: 1, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5 }}>Target</Typography>
              <Typography variant="caption" sx={{ width: 62, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5 }}>Conf</Typography>
              <Typography variant="caption" sx={{ width: 32, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5, textAlign: 'right' }}>Age</Typography>
              <Typography variant="caption" sx={{ width: 42, flexShrink: 0, fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5, textAlign: 'right' }}>#</Typography>
            </Box>

            {/* Table rows */}
            {filtered.map(alert => {
              const threat = getThreatStatus(alert.confidence_score);
              const isExpanded = expandedId === alert.id;
              const evidence = alert.evidence || {};
              const confPct = Math.round((alert.confidence_score ?? 0) * 100);
              const currentFeedback = feedbackSubmitted[alert.id] || alert.feedback_verdict;

              return (
                <Box key={alert.id} sx={{ borderBottom: '1px solid', borderColor: 'divider',
                  ...(threat.label === 'CONFIRMED' && { bgcolor: `${threat.color}06` }),
                }}>
                  {/* Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.6, cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: isDark ? `${threat.color}12` : `${threat.color}06` },
                  }} onClick={() => {
                    const expanding = !isExpanded;
                    setExpandedId(expanding ? alert.id : null);
                    onAlertClick?.(alert, expanding);
                    if (expanding) handleLoadExplanation(alert.id);
                  }}>
                    {/* Severity dot */}
                    <Box sx={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: threat.color,
                        ...(threat.label === 'CONFIRMED' && { boxShadow: `0 0 6px ${threat.color}` }),
                      }} />
                    </Box>
                    {/* Type */}
                    <Typography variant="caption" fontWeight={600} noWrap sx={{ width: 125, flexShrink: 0, color: threat.color, fontSize: '0.7rem' }}>
                      {getAnomalyLabel(alert.anomaly_type)}
                      {evidence.contributing_rules && evidence.contributing_rules.length > 1 && (
                        <Typography component="span" variant="caption" sx={{ ml: 0.5, fontSize: '0.55rem', opacity: 0.6 }}>
                          +{evidence.contributing_rules.length - 1}
                        </Typography>
                      )}
                    </Typography>
                    {/* Signal */}
                    <Box sx={{ width: 42, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: SIGNAL_DOT_COLOR[alert.signal_type] || '#757575' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{SIGNAL_ABBREV[alert.signal_type] || alert.signal_type}</Typography>
                    </Box>
                    {/* Target */}
                    <Typography variant="caption" noWrap sx={{ flex: 1, fontFamily: '"Roboto Mono", monospace', fontSize: '0.65rem', opacity: 0.85, pr: 0.5 }}>
                      {getAlertTarget(alert)}
                    </Typography>
                    {/* Confidence bar */}
                    <Box sx={{ width: 62, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ flex: 1, height: 4, bgcolor: `${threat.color}20`, borderRadius: 2 }}>
                        <Box sx={{ width: `${confPct}%`, height: '100%', bgcolor: threat.color, borderRadius: 2, transition: 'width 0.3s' }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: threat.color, minWidth: 26, textAlign: 'right' }}>{confPct}%</Typography>
                    </Box>
                    {/* Age (hover for exact detected-at) */}
                    <Tooltip title={
                      <Box sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
                        <div><strong>Detected:</strong> {formatExact(alert.detected_at)}</div>
                        {(evidence.active_from || alert.first_seen) && (
                          <div>
                            <strong>Active:</strong> {formatExact(evidence.active_from || alert.first_seen)}
                            {(evidence.active_to || alert.last_seen) ? ` – ${formatExact(evidence.active_to || alert.last_seen)}` : ''}
                          </div>
                        )}
                        {alert.last_seen && alert.last_seen !== alert.detected_at && !evidence.active_to && (
                          <div><strong>Last seen:</strong> {formatExact(alert.last_seen)}</div>
                        )}
                        {(alert as any).resolved_at && (
                          <div><strong>Resolved:</strong> {formatExact((alert as any).resolved_at)}</div>
                        )}
                      </Box>
                    } placement="left" arrow>
                      <Typography variant="caption" sx={{ width: 32, flexShrink: 0, textAlign: 'right', fontSize: '0.6rem', opacity: 0.7, cursor: 'help' }}>
                        {formatTimeCompact(alert.detected_at)}
                      </Typography>
                    </Tooltip>
                    {/* Count */}
                    <Typography variant="caption" sx={{ width: 42, flexShrink: 0, textAlign: 'right', fontSize: '0.6rem',
                      fontWeight: alert.observation_count && alert.observation_count > 1 ? 700 : 400,
                      opacity: alert.observation_count && alert.observation_count > 1 ? 1 : 0.4,
                    }}>
                      {alert.observation_count && alert.observation_count > 1 ? (
                        <Chip size="small" label={`x${alert.observation_count}`}
                          sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700 }} />
                      ) : '—'}
                    </Typography>
                  </Box>

                  {/* Expanded detail */}
                  <Collapse in={isExpanded}>
                    <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.75rem' }}>
                        {(evidence.rule || evidence.primary_rule) && (
                          <Typography variant="caption"><strong>Rule:</strong> {evidence.rule || evidence.primary_rule}</Typography>
                        )}
                        {evidence.contributing_rules && evidence.contributing_rules.length > 1 && (
                          <Typography variant="caption" sx={{ opacity: 0.7 }}><strong>Contributing rules:</strong> {evidence.contributing_rules.join(', ')}</Typography>
                        )}
                        {evidence.mcc !== undefined && <Typography variant="caption"><strong>MCC/MNC:</strong> {evidence.mcc}/{evidence.mnc}{evidence.network_type ? ` | ${evidence.network_type}` : ''}</Typography>}
                        {evidence.pci !== undefined && <Typography variant="caption"><strong>PCI:</strong> {evidence.pci} | <strong>ARFCN:</strong> {evidence.nrarfcn ?? 'N/A'} | <strong>Band:</strong> {evidence.arfcn_band || 'N/A'}</Typography>}
                        {evidence.ss_rsrq != null && <Typography variant="caption"><strong>RSRQ:</strong> {evidence.ss_rsrq} dB{evidence.signal_level != null ? ` | Level: ${evidence.signal_level}` : ''}</Typography>}
                        {evidence.is_registered !== undefined && <Typography variant="caption"><strong>Registered:</strong> {String(evidence.is_registered)}{evidence.null_fields ? ` | Null: ${evidence.null_fields.join(', ')}` : ''}</Typography>}
                        {evidence.bssid && <Typography variant="caption"><strong>BSSID:</strong> {evidence.bssid}{evidence.is_la_bit ? ' (LA-bit)' : ''}</Typography>}
                        {evidence.ssid !== undefined && alert.signal_type === 'wifi' && <Typography variant="caption"><strong>SSID:</strong> {evidence.ssid || '<hidden>'}</Typography>}
                        {evidence.security !== undefined && <Typography variant="caption"><strong>Security:</strong> {evidence.security || 'Open'}</Typography>}
                        {evidence.oui_manufacturer && alert.signal_type === 'wifi' && <Typography variant="caption"><strong>AP Mfr:</strong> {evidence.oui_manufacturer}</Typography>}
                        {evidence.is_connected && <Typography variant="caption" sx={{ color: '#ff5722' }}><strong>Connected:</strong>{evidence.link_speed_mbps ? ` ${evidence.link_speed_mbps} Mbps` : ''}{evidence.connected_frequency ? ` @ ${evidence.connected_frequency} MHz` : ''}{evidence.connected_rssi != null ? ` (${evidence.connected_rssi} dBm)` : ''}</Typography>}
                        {evidence.is_80211az_ntb_responder && <Typography variant="caption"><strong>802.11az NTB:</strong> Yes</Typography>}
                        {evidence.address && <Typography variant="caption"><strong>Address:</strong> {evidence.address}{evidence.is_rotating_mac ? ' (rotating)' : ''}{!evidence.is_rotating_mac && evidence.address_type ? ` [${evidence.address_type}]` : ''}{evidence.is_trackable === true ? ' — trackable' : ''}</Typography>}
                        {evidence.name !== undefined && alert.signal_type === 'bluetooth' && <Typography variant="caption"><strong>Name:</strong> {evidence.name || '<none>'}</Typography>}
                        {evidence.rssi != null && <Typography variant="caption"><strong>RSSI:</strong> {evidence.rssi} dBm</Typography>}
                        {evidence.bt_type && <Typography variant="caption"><strong>Type:</strong> {evidence.bt_type}{evidence.bond_state ? ` | Bond: ${evidence.bond_state}` : ''}</Typography>}
                        {evidence.device_class_major && <Typography variant="caption"><strong>Class:</strong> {evidence.device_class_major}/{evidence.device_class_minor}</Typography>}
                        {evidence.oui_manufacturer && alert.signal_type === 'bluetooth' && <Typography variant="caption"><strong>OUI:</strong> {evidence.oui_manufacturer}</Typography>}
                        {(evidence.primary_phy || evidence.secondary_phy) && <Typography variant="caption"><strong>PHY:</strong> {evidence.primary_phy || '—'}{evidence.secondary_phy ? ` / ${evidence.secondary_phy}` : ''}</Typography>}
                        {evidence.advertising_sid != null && <Typography variant="caption"><strong>Adv SID:</strong> {evidence.advertising_sid}</Typography>}
                        {evidence.service_data_map && <Typography variant="caption" sx={{ opacity: 0.7 }}><strong>Svc Data:</strong> {evidence.service_data_map.length > 50 ? evidence.service_data_map.substring(0, 50) + '...' : evidence.service_data_map}</Typography>}
                        {evidence.cn0_dbhz !== undefined && <Typography variant="caption"><strong>C/N0:</strong> {typeof evidence.cn0_dbhz === 'number' ? evidence.cn0_dbhz.toFixed(1) : evidence.cn0_dbhz} dB-Hz{evidence.threshold ? ` (thr: ${evidence.threshold})` : ''}</Typography>}
                        {evidence.constellation && <Typography variant="caption"><strong>Constellation:</strong> {evidence.constellation}{evidence.svid != null ? ` SV${evidence.svid}` : ''}</Typography>}
                        {evidence.satellites_in_view != null && <Typography variant="caption"><strong>Sats:</strong> {evidence.satellites_used}/{evidence.satellites_in_view}{evidence.exclusion_ratio != null ? ` (${(evidence.exclusion_ratio * 100).toFixed(0)}% excl)` : ''}</Typography>}
                        {evidence.drone_manufacturer && <Typography variant="caption" sx={{ color: '#e91e63' }}><strong>Drone:</strong> {evidence.drone_manufacturer}{evidence.product_family ? ` — ${evidence.product_family}` : ''}</Typography>}
                        {evidence.uas_id && <Typography variant="caption" sx={{ color: '#e91e63' }}><strong>UAS:</strong> {evidence.uas_id}{evidence.drone_status ? ` | ${evidence.drone_status}` : ''}</Typography>}
                        {evidence.drone_lat != null && <Typography variant="caption"><strong>Drone pos:</strong> {Number(evidence.drone_lat).toFixed(5)}, {Number(evidence.drone_lon).toFixed(5)}{evidence.drone_alt_geo != null ? ` alt ${evidence.drone_alt_geo}m` : ''}{evidence.speed != null ? ` ${evidence.speed}m/s` : ''}</Typography>}
                        {evidence.sighting_count != null && <Typography variant="caption"><strong>Sightings:</strong> {evidence.sighting_count} across {evidence.distinct_locations} locations</Typography>}
                        {evidence.spread_meters != null && <Typography variant="caption"><strong>Spread:</strong> {Number(evidence.spread_meters).toFixed(0)}m{evidence.position_stddev_m != null ? ` | σ ${Number(evidence.position_stddev_m).toFixed(0)}m` : ''}</Typography>}
                        {evidence.avg_distance_mm != null && <Typography variant="caption"><strong>RTT:</strong> {(Number(evidence.avg_distance_mm) / 1000).toFixed(1)}m avg{evidence.distance_cv != null ? ` | CV ${Number(evidence.distance_cv).toFixed(2)}` : ''}</Typography>}
                        {evidence.external_id && <Typography variant="caption"><strong>Ext ID:</strong> {evidence.external_id}{evidence.ssid ? ` (${evidence.ssid})` : ''}</Typography>}
                        {evidence.method && evidence.estimated_lat != null && <Typography variant="caption" sx={{ color: '#00bcd4' }}><strong>Located:</strong> {Number(evidence.estimated_lat).toFixed(5)}, {Number(evidence.estimated_lon).toFixed(5)}{evidence.accuracy_m != null ? ` ±${evidence.accuracy_m}m` : ''} — {evidence.method}{evidence.phone_count ? ` (${evidence.phone_count} phones)` : ''}</Typography>}
                        {evidence.geofence_facility && <Typography variant="caption" sx={{ color: '#ff9800' }}><strong>Geofence:</strong> {evidence.geofence_facility} ({evidence.geofence_sensitivity})</Typography>}
                        {evidence.stable_identifier && <Typography variant="caption" sx={{ opacity: 0.6 }}><strong>Stable ID:</strong> {evidence.stable_identifier}</Typography>}
                        {isExpanded && <VerdictPanel alert={alert} evidence={evidence} onEmitterLocated={onEmitterLocated} />}
                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.5, mt: 0.5 }}>
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            <strong>Detected:</strong> {formatExact(alert.detected_at)}
                          </Typography>
                          {(evidence.active_from || alert.first_seen) && (
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                              <strong>Active:</strong> {formatExact(evidence.active_from || alert.first_seen)}
                              {(evidence.active_to || alert.last_seen) ? ` – ${formatExact(evidence.active_to || alert.last_seen)}` : ''}
                              {alert.observation_count && alert.observation_count > 1 ? ` (${alert.observation_count} observations)` : ''}
                            </Typography>
                          )}
                          {alert.last_seen && alert.last_seen !== alert.detected_at && !evidence.active_from && !alert.first_seen && (
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                              <strong>Last seen:</strong> {formatExact(alert.last_seen)}
                              {alert.observation_count && alert.observation_count > 1 ? ` (${alert.observation_count} observations)` : ''}
                            </Typography>
                          )}
                          {(alert as any).resolved_at && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                              <strong>Resolved:</strong> {formatExact((alert as any).resolved_at)}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 0.25 }}>
                            <strong>Conf:</strong> {((alert.confidence_score || 0) * 100).toFixed(1)}%
                            {evidence.raw_confidence != null && ` (raw ${(evidence.raw_confidence * 100).toFixed(1)}%)`}
                            {evidence.geofence_multiplier != null && evidence.geofence_multiplier !== 1 && ` ×${evidence.geofence_multiplier}`}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
                            <strong>Device:</strong> {deviceIdToEntity?.[alert.device_id] ? `${deviceIdToEntity[alert.device_id]} (${alert.device_id})` : alert.device_id}
                            {(() => {
                              const lat = alert.latitude ?? evidence.watchlist_latitude;
                              const lon = alert.longitude ?? evidence.watchlist_longitude;
                              if (lat == null || lon == null) return '';
                              const place = `${Number(lat).toFixed(5)}, ${Number(lon).toFixed(5)}`;
                              return evidence.location_source === 'last_known' ? ` | ${place} (last known)` : ` | ${place}`;
                            })()}
                          </Typography>
                        </Box>
                        {isExpanded && (
                          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.5, mt: 0.5 }}>
                            {!explanations[alert.id] && (
                              <Button size="small" variant="text"
                                onClick={e => { e.stopPropagation(); handleLoadExplanation(alert.id); }}
                                disabled={loadingExplanation === alert.id}
                                sx={{ fontSize: '0.7rem', textTransform: 'none', opacity: 0.75, p: 0, minWidth: 0 }}>
                                {loadingExplanation === alert.id ? 'Loading...' : '▶ Why was this flagged?'}
                              </Button>
                            )}
                            {explanations[alert.id] && (() => {
                              const exp = explanations[alert.id];
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: `${threat.color}10`, border: `1px solid ${threat.color}30` }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: threat.color, display: 'block', mb: 0.25 }}>Why this was flagged</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>{exp.why}</Typography>
                                  </Box>
                                  <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.04)' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>What it means</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5, opacity: 0.85 }}>{exp.what_it_means}</Typography>
                                  </Box>
                                  {exp.false_positive_hints?.length > 0 && (
                                    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(33,150,243,0.06)', border: '1px solid rgba(33,150,243,0.2)' }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#42a5f5', display: 'block', mb: 0.25 }}>Possible innocent explanations</Typography>
                                      {exp.false_positive_hints.map((hint: string, i: number) => (
                                        <Typography key={i} variant="caption" sx={{ display: 'block', lineHeight: 1.5, opacity: 0.8 }}>• {hint}</Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {alert.latitude && alert.longitude && (
                          <Button size="small" variant="outlined" startIcon={<LocationIcon />}
                            onClick={e => { e.stopPropagation(); onAlertClick?.(alert, true); }}
                            sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Map</Button>
                        )}
                        <Button size="small" variant="outlined" color="success" startIcon={<AckIcon />}
                          onClick={e => handleAcknowledge(alert.id, e)}
                          sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Ack</Button>
                        {intelEnabled && (
                          <Button size="small" variant="outlined" startIcon={<FactCheckIcon />}
                            onClick={e => {
                              e.stopPropagation();
                              setPromoteTarget({
                                member_type: 'dfp_alert',
                                member_id: String(alert.id),
                                label: `${alert.anomaly_type || 'DFP alert'} ${alert.device_id || ''}`.trim(),
                                is_primary: true,
                                meta: { device_id: alert.device_id, severity: alert.severity, latitude: alert.latitude, longitude: alert.longitude },
                              });
                            }}
                            sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Add to packet</Button>
                        )}
                        {ALERT_EXPLANATIONS[alert.anomaly_type] && (
                          <Button size="small" variant="text" startIcon={<HelpIcon />}
                            onClick={e => { e.stopPropagation(); setAlertExplainType(alert.anomaly_type); }}
                            sx={{ fontSize: '0.7rem', textTransform: 'none', opacity: 0.7 }}>Info</Button>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', alignItems: 'center' }}>
                          <Tooltip title="Add this identifier to known-device exclusions">
                            <Chip size="small" label="Exclude" icon={<BlockIcon />}
                              clickable disabled={quickActionSaving}
                              onClick={e => openExclusionDialog(alert, e)}
                              sx={{ height: 18, fontSize: '0.55rem', bgcolor: 'rgba(144,202,249,0.10)', color: '#90caf9', cursor: 'pointer', '& .MuiChip-icon': { fontSize: 12, color: '#90caf9' } }} />
                          </Tooltip>
                          {buildTrustedWifiDraft(alert) && (
                            <Tooltip title="Create a trusted WiFi profile from this alert">
                              <Chip size="small" label="Trust" icon={<TrustedIcon />}
                                clickable disabled={quickActionSaving}
                                onClick={e => openTrustedWifiDialog(alert, e)}
                                sx={{ height: 18, fontSize: '0.55rem', bgcolor: 'rgba(76,175,80,0.10)', color: '#4caf50', cursor: 'pointer', '& .MuiChip-icon': { fontSize: 12, color: '#4caf50' } }} />
                            </Tooltip>
                          )}
                          <Tooltip title="Add this device to the threat watchlist">
                            <Chip size="small" label="Watchlist" icon={<WatchlistIcon />}
                              clickable disabled={quickActionSaving}
                              onClick={e => openWatchlistDialog(alert, e)}
                              sx={{ height: 18, fontSize: '0.55rem', bgcolor: 'rgba(244,67,54,0.10)', color: '#f44336', cursor: 'pointer', '& .MuiChip-icon': { fontSize: 12, color: '#f44336' } }} />
                          </Tooltip>
                          {currentFeedback ? (
                            <Chip size="small"
                              label={currentFeedback === 'true_positive' ? '✓ TP' : currentFeedback === 'false_positive' ? '✗ FP' : '?'}
                              sx={{ height: 18, fontSize: '0.55rem',
                                bgcolor: currentFeedback === 'true_positive' ? 'rgba(244,67,54,0.15)' : currentFeedback === 'false_positive' ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)',
                                color: currentFeedback === 'true_positive' ? '#f44336' : currentFeedback === 'false_positive' ? '#4caf50' : '#ff9800',
                              }} />
                          ) : (
                            <>
                              <Tooltip title="True positive"><Chip size="small" label="TP" clickable disabled={feedbackLoading === alert.id} onClick={e => handleFeedback(alert.id, 'true_positive', e)} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336', cursor: 'pointer' }} /></Tooltip>
                              <Tooltip title="False positive"><Chip size="small" label="FP" clickable disabled={feedbackLoading === alert.id} onClick={e => handleFeedback(alert.id, 'false_positive', e)} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50', cursor: 'pointer' }} /></Tooltip>
                              <Tooltip title="Unclear"><Chip size="small" label="?" clickable disabled={feedbackLoading === alert.id} onClick={e => handleFeedback(alert.id, 'unclear', e)} sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: 'rgba(255,152,0,0.1)', color: '#ff9800', cursor: 'pointer' }} /></Tooltip>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog open={!!exclusionDraft} onClose={() => !quickActionSaving && setExclusionDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add DFP Exclusion</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <Alert severity="info">
              This suppresses matching known identifiers in future DFP detections. Review the auto-filled target before submitting.
            </Alert>
            {quickActionError && <Alert severity="error">{quickActionError}</Alert>}
            <TextField
              size="small"
              label="Identifier"
              value={exclusionDraft?.identifier || ''}
              onChange={e => setExclusionDraft(prev => prev ? ({ ...prev, identifier: e.target.value, identifier_type: inferIdentifierType(e.target.value) }) : prev)}
              fullWidth
              required
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={exclusionDraft?.identifier_type || 'mac'}
                  onChange={e => setExclusionDraft(prev => prev ? ({ ...prev, identifier_type: e.target.value as DfpExclusionIdentifierType }) : prev)}
                >
                  <MenuItem value="mac">MAC</MenuItem>
                  <MenuItem value="ip">IP</MenuItem>
                  <MenuItem value="device_id">Device ID</MenuItem>
                  <MenuItem value="ssid">SSID</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  label="Scope"
                  value={exclusionDraft?.applies_to || 'all'}
                  onChange={e => setExclusionDraft(prev => prev ? ({ ...prev, applies_to: e.target.value as DfpExclusionScope }) : prev)}
                >
                  <MenuItem value="cotraveler">Co-traveler only</MenuItem>
                  <MenuItem value="wifi">WiFi only</MenuItem>
                  <MenuItem value="all">All DFP detections</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              size="small"
              label="Label"
              value={exclusionDraft?.label || ''}
              onChange={e => setExclusionDraft(prev => prev ? ({ ...prev, label: e.target.value }) : prev)}
              fullWidth
            />
            <TextField
              size="small"
              label="Reason"
              placeholder="Why this should be excluded"
              value={exclusionDraft?.reason || ''}
              onChange={e => setExclusionDraft(prev => prev ? ({ ...prev, reason: e.target.value }) : prev)}
              fullWidth
              required
              multiline
              minRows={2}
              error={!!exclusionDraft && !exclusionDraft.reason.trim()}
              helperText="Required for auditability"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExclusionDraft(null)} disabled={quickActionSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitExclusion}
            disabled={quickActionSaving || !exclusionDraft?.identifier.trim() || !exclusionDraft?.reason.trim()}
            startIcon={quickActionSaving ? <CircularProgress size={16} /> : <BlockIcon />}
          >
            Add Exclusion
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!trustedWifiDraft} onClose={() => !quickActionSaving && setTrustedWifiDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Trusted WiFi Profile</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <Alert severity="info">
              Trusted profiles reduce known network noise here, while still allowing spoofing alerts when the SSID/BSSID appears in unexpected places.
            </Alert>
            {quickActionError && <Alert severity="error">{quickActionError}</Alert>}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="SSID"
                value={trustedWifiDraft?.ssid || ''}
                onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, ssid: e.target.value }) : prev)}
                fullWidth
                required
              />
              <TextField
                size="small"
                label="BSSID"
                value={trustedWifiDraft?.bssid || ''}
                onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, bssid: e.target.value }) : prev)}
                fullWidth
              />
            </Box>
            <TextField
              size="small"
              label="Label"
              value={trustedWifiDraft?.label || ''}
              onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, label: e.target.value }) : prev)}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Latitude"
                value={trustedWifiDraft?.latitude || ''}
                onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, latitude: e.target.value }) : prev)}
                fullWidth
              />
              <TextField
                size="small"
                label="Longitude"
                value={trustedWifiDraft?.longitude || ''}
                onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, longitude: e.target.value }) : prev)}
                fullWidth
              />
              <TextField
                size="small"
                label="Radius m"
                value={trustedWifiDraft?.radius_m || '150'}
                onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, radius_m: e.target.value }) : prev)}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="Security"
                value={trustedWifiDraft?.security || ''}
                onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, security: e.target.value }) : prev)}
                fullWidth
              />
              <TextField
                size="small"
                label="Gateway IP"
                value={trustedWifiDraft?.gateway_ip || ''}
                onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, gateway_ip: e.target.value }) : prev)}
                fullWidth
              />
            </Box>
            <TextField
              size="small"
              label="Reason"
              placeholder="Why this WiFi fingerprint is trusted"
              value={trustedWifiDraft?.reason || ''}
              onChange={e => setTrustedWifiDraft(prev => prev ? ({ ...prev, reason: e.target.value }) : prev)}
              fullWidth
              required
              multiline
              minRows={2}
              error={!!trustedWifiDraft && !trustedWifiDraft.reason.trim()}
              helperText="Required for auditability"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrustedWifiDraft(null)} disabled={quickActionSaving}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={submitTrustedWifi}
            disabled={quickActionSaving || !trustedWifiDraft?.ssid.trim() || !trustedWifiDraft?.reason.trim()}
            startIcon={quickActionSaving ? <CircularProgress size={16} /> : <TrustedIcon />}
          >
            Trust WiFi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to watchlist dialog */}
      <Dialog open={!!watchlistDraft} onClose={() => !quickActionSaving && setWatchlistDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WatchlistIcon sx={{ color: '#f44336' }} />
          Add Device to Threat Watchlist
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              A hit on any selected identifier raises a watchlist alert. Stable IDs are
              checked; rotating hotspot or BLE addresses are left off.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {watchlistDraft?.identities.map((row, index) => (
                <FormControlLabel
                  key={`${row.signal_type}:${row.identifier_type}:${row.identifier}`}
                  sx={{ alignItems: 'flex-start', mr: 0, ml: 0 }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={row.selected}
                      onChange={(_, checked) => setWatchlistDraft((prev) => prev ? ({
                        ...prev,
                        identities: prev.identities.map((item, i) => (
                          i === index ? { ...item, selected: checked } : item
                        )),
                      }) : prev)}
                      sx={{ pt: 0.25 }}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {row.identifier}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.identifier_type} · {row.signal_type} — {row.note}
                      </Typography>
                    </Box>
                  )}
                />
              ))}
            </Box>
            <FormControl size="small" sx={{ maxWidth: 180 }}>
              <InputLabel>Severity</InputLabel>
              <Select
                label="Severity"
                value={watchlistDraft?.severity_floor || 'critical'}
                onChange={e => setWatchlistDraft(prev => prev ? ({ ...prev, severity_floor: e.target.value as WatchlistBundle['severity_floor'] }) : prev)}
              >
                {(['info', 'warning', 'critical'] as const).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Label"
              value={watchlistDraft?.label || ''}
              onChange={e => setWatchlistDraft(prev => prev ? ({ ...prev, label: e.target.value }) : prev)}
              fullWidth
            />
            <TextField
              size="small"
              label="Reason"
              placeholder="Why this device is a threat"
              value={watchlistDraft?.reason || ''}
              onChange={e => setWatchlistDraft(prev => prev ? ({ ...prev, reason: e.target.value }) : prev)}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWatchlistDraft(null)} disabled={quickActionSaving}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitWatchlist}
            disabled={quickActionSaving || !watchlistDraft || selectedWatchlistIdentities(watchlistDraft).length === 0}
            startIcon={quickActionSaving ? <CircularProgress size={16} /> : <WatchlistIcon />}
          >
            Add to Watchlist
          </Button>
        </DialogActions>
      </Dialog>

      {/* How Alerts Work dialog */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon sx={{ color: '#2196f3' }} />
          How Signal Threat Alerts Work
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            The DFP (Digital Force Protection) detection engine continuously analyzes RF telemetry from operator devices —
            cellular, WiFi, Bluetooth, and GNSS signals — to identify electromagnetic threats. The engine runs <strong>23+ detection rules</strong> across <strong>18 anomaly types</strong> with compound escalation, multi-phone TDOA positioning, and geofence-aware scoring.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Collection app v8.7+</strong> adds WiFi RTT (802.11mc), Drone Remote ID (ASTM F3411), 6 new BLE DFP fields (addressType, PHY, advertisingSid, serviceDataMap), 8 new WiFi DFP fields (802.11az, connected network details), and 1-second collection intervals.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#2196f3', mt: 2 }}>Alert Lifecycle</Typography>
          <Typography variant="body2" paragraph>
            <strong>1. Ingest:</strong> Signals arrive via MQTT and are stored in signal tables (WiFi, Cellular, Bluetooth, GNSS, Drone RemoteID).{' '}
            <strong>2. Enrich:</strong> Each signal is enriched with reference data — OUI lookups, geofence membership, known device matching, and scan context.{' '}
            <strong>3. Detect:</strong> All active detectors run against each enriched event.{' '}
            <strong>4. Fuse:</strong> Results are scored, geofence-modulated, and grouped into alerts.{' '}
            <strong>5. Persist:</strong> Alerts are deduplicated and stored. Repeat observations increment the count.{' '}
            <strong>6. Resolve:</strong> Stale alerts are automatically resolved after the suppression window.
          </Typography>
          <Typography variant="body2" paragraph>
            Resolution windows: <strong>Critical</strong> = 30 min, <strong>Warning</strong> = 60 min, <strong>Info</strong> = 120 min after the last observation.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#f44336', mt: 2 }}>WiFi Threats — 8 Rules</Typography>
          <Box sx={{ pl: 1, mb: 1.5 }}>
            <Typography variant="caption" display="block"><strong>Rogue AP (LA-bit BSSID)</strong> — Locally administered MAC on AP. Higher confidence inside geofences. (0.30-0.90)</Typography>
            <Typography variant="caption" display="block"><strong>Open Non-IoT AP</strong> — Unencrypted AP not matching known IoT patterns. (0.30)</Typography>
            <Typography variant="caption" display="block"><strong>Foreign Country AP</strong> — 802.11 IE country code is not "US". Decoded from Information Elements. (0.70)</Typography>
            <Typography variant="caption" display="block"><strong>Hidden + High Power</strong> — Hidden SSID with TX power &gt; 24 dBm. Classic rogue AP signature. (0.60)</Typography>
            <Typography variant="caption" display="block"><strong>IE Open Network</strong> — No RSN Information Element present (unencrypted). (0.40)</Typography>
            <Typography variant="caption" display="block"><strong>Evil Twin</strong> — Same SSID on multiple BSSIDs within a 30-second scan window. (0.50-1.0)</Typography>
            <Typography variant="caption" display="block"><strong>Multichannel Honeypot</strong> — Same SSID broadcasting on 3+ channels simultaneously. (0.50-1.0)</Typography>
            <Typography variant="caption" display="block"><strong>Connection Downgrade</strong> — Connected AP link speed or frequency dropped significantly (e.g. 5 GHz → 2.4 GHz). Requires v8.7+ collection app. (0.50-0.75)</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#ff5722' }}>Cellular Threats — 5 Rules</Typography>
          <Box sx={{ pl: 1, mb: 1.5 }}>
            <Typography variant="caption" display="block"><strong>Unknown PCI</strong> — Physical Cell ID not in reference database. (0.85-0.92)</Typography>
            <Typography variant="caption" display="block"><strong>Foreign MCC</strong> — Mobile Country Code from non-domestic network. (0.70)</Typography>
            <Typography variant="caption" display="block"><strong>Null Cell Tower ID</strong> — Valid RF signal but no cell tower identity. (0.60)</Typography>
            <Typography variant="caption" display="block"><strong>Unlicensed ARFCN</strong> — NR-ARFCN outside licensed band allocations. (0.75)</Typography>
            <Typography variant="caption" display="block"><strong>Unregistered Operator</strong> — MCC/MNC not in known operator database. (0.40-0.55)</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#3f51b5' }}>Bluetooth Threats — 3 Rules</Typography>
          <Box sx={{ pl: 1, mb: 1.5 }}>
            <Typography variant="caption" display="block"><strong>Known Tracker OUI</strong> — BLE device matches AirTag/Tile/SmartTag manufacturer. (0.60)</Typography>
            <Typography variant="caption" display="block"><strong>Unknown Persistent Device</strong> — Unrecognized BLE device following operator over time/distance. (0.30-0.80)</Typography>
            <Typography variant="caption" display="block"><strong>MAC Anomaly</strong> — Unknown OUI with strong signal (close proximity). Uses addressType for confidence scoring (PUBLIC = persistently trackable). (0.25)</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#009688' }}>GNSS Threats — 3 Rules</Typography>
          <Box sx={{ pl: 1, mb: 1.5 }}>
            <Typography variant="caption" display="block"><strong>GNSS Jamming</strong> — CN0 below threshold across satellites. Confidence scales with severity. (0.30-0.90)</Typography>
            <Typography variant="caption" display="block"><strong>SV Exclusion Spoofing</strong> — High satellite exclusion ratio with adequate SVs in view. (0.55)</Typography>
            <Typography variant="caption" display="block"><strong>Clock Coherence Spoofing</strong> — Anomalous code lock + time-of-week patterns. (0.40)</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#795548' }}>Drone, Behavioral, Sensor — 4 Rules</Typography>
          <Box sx={{ pl: 1, mb: 1.5 }}>
            <Typography variant="caption" display="block"><strong>RemoteID Broadcast</strong> — FAA RemoteID drone beacon with operator/location data. (0.50-1.0)</Typography>
            <Typography variant="caption" display="block"><strong>BLE/WiFi Drone Signature</strong> — OUI + name matching DJI/Parrot/Skydio/etc. (0.40-0.90)</Typography>
            <Typography variant="caption" display="block"><strong>Co-Traveler</strong> — External device following operator across multiple locations. (0.30-1.0)</Typography>
            <Typography variant="caption" display="block"><strong>Abnormal Signal</strong> — -127 dBm sentinel values or unknown connected SSID. (0.15-0.20)</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#00bcd4', mt: 1 }}>Multi-Phone Emitter Positioning (TDOA)</Typography>
          <Box sx={{ pl: 1, mb: 1.5 }}>
            <Typography variant="caption" display="block"><strong>BLE Located</strong> — BLE device positioned via multi-phone RSSI centroid or differential TDOA (nanosecond timing).</Typography>
            <Typography variant="caption" display="block"><strong>WiFi AP Located</strong> — Access point positioned from multi-phone signal strength observations.</Typography>
            <Typography variant="caption" display="block"><strong>Cell Tower Located</strong> — Base station triangulated; position mismatch vs FCC database indicates rogue infrastructure.</Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#e91e63', mt: 1 }}>Compound Escalation</Typography>
          <Typography variant="body2" paragraph>
            When <strong>IMSI catcher suspect + ephemeral cell tower</strong> converge on the same PCI, confidence is escalated to <strong>0.95 / Critical</strong>. Either alone has innocent explanations; both together is near-confirmed hostile infrastructure.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ color: '#00bcd4', mt: 1 }}>802.11 Information Element Analysis</Typography>
          <Typography variant="body2" paragraph>
            WiFi signals include raw 802.11 Information Elements decoded for threat assessment. The Signal Intelligence panel (available in COP Waterfall, Pathfinder, and Link Analysis) shows decoded security protocol, ciphers, WiFi generation, country code, transmit power, station count, and vendor fingerprints. Threat indicators include foreign country codes, missing encryption, hidden SSIDs with high power, and downgrade attack surfaces. Collection app v8.7+ adds WiFi RTT (802.11mc) piggybacked on scans, 802.11az NTB responder detection, and connected network details for downgrade detection.
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Severity Levels</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label="CRITICAL" sx={{ bgcolor: 'rgba(244,67,54,0.15)', color: '#f44336', fontWeight: 'bold' }} />
              <Typography variant="caption">Score 0.70-1.0 — Immediate action required (rogue base station, active jamming, confirmed co-traveler, compound escalation)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label="WARNING" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ff9800', fontWeight: 'bold' }} />
              <Typography variant="caption">Score 0.40-0.69 — Suspicious activity warranting analyst review (GNSS spoofing, foreign AP, evil twin, co-traveler)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip size="small" label="INFO" sx={{ bgcolor: 'rgba(33,150,243,0.15)', color: '#2196f3', fontWeight: 'bold' }} />
              <Typography variant="caption">Score 0.0-0.39 — Low-severity indicators for situational awareness</Typography>
            </Box>
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Show All Toggle</Typography>
          <Typography variant="body2" paragraph>
            Enable <strong>Show All</strong> to see both open and resolved alerts. Resolved alerts appear with a dimmed badge so you can review the full threat history.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>Analyst Feedback</Typography>
          <Typography variant="body2" paragraph>
            Use the <strong>TP</strong> (True Positive), <strong>FP</strong> (False Positive), and <strong>?</strong> (Unclear) buttons on each alert to provide feedback. This data is used to evaluate detection rule effectiveness and tune confidence thresholds over time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Alert type explanation dialog */}
      <Dialog open={!!alertExplainType} onClose={() => setAlertExplainType(null)} maxWidth="sm" fullWidth>
        {alertExplainType && ALERT_EXPLANATIONS[alertExplainType] && (() => {
          const ex = ALERT_EXPLANATIONS[alertExplainType];
          return (
            <>
              <DialogTitle>{ex.title}</DialogTitle>
              <DialogContent dividers>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#2196f3' }}>What is it?</Typography>
                <Typography variant="body2" paragraph>{ex.what}</Typography>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#ff9800' }}>Risk</Typography>
                <Typography variant="body2" paragraph>{ex.risk}</Typography>
                <Typography variant="subtitle2" gutterBottom sx={{ color: '#4caf50' }}>Recommended Action</Typography>
                <Typography variant="body2" paragraph>{ex.action}</Typography>
                {ex.rules && ex.rules.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: '#9c27b0', mt: 1 }}>Detection Rules (Confidence)</Typography>
                    {ex.rules.map((rule, i) => (
                      <Typography key={i} variant="caption" display="block" sx={{ pl: 1, mb: 0.5, color: 'text.secondary' }}>
                        {rule}
                      </Typography>
                    ))}
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAlertExplainType(null)}>Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <PromoteToPacketDialog
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        target={promoteTarget}
        defaultName={promoteTarget?.label ? `DFP: ${promoteTarget.label}` : undefined}
      />

    </Box>
  );
};

export default DFPAlertsTab;
